import { Box, Fade, Slide, useTheme } from '@mui/material';
import { ArrowLeft, Close } from '@portals/icons';
import React, { KeyboardEvent, MouseEventHandler, ReactNode, SyntheticEvent, useCallback, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';

import { Header } from '../../../../containers/Header/Header';
import type { Theme } from '../../../../themes/themes';
import { Button } from '../../../Button/Button';
import { Divider } from '../../../Divider/Divider';
import { Flyout } from '../../../Flyout/Flyout';
import { HeaderBar, HeaderBarProps } from '../../../HeaderBar/HeaderBar';
import { IconButton } from '../../../IconButton/IconButton';
import { MenuLinkList } from '../../../MenuLinkList/MenuLinkList';
import { FlatTree, isLast, itemsByParent } from '../../flatTree';
import { getStyles } from './Mobile.styles';

export interface MobileProps {
  tree: FlatTree[];
  open: boolean;
  HeaderBarProps: HeaderBarProps;
  onClose: MouseEventHandler<HTMLOrSVGElement>;
  MobileProps?: {
    customPanel?: ReactNode;
    onCustomPanelBack?: () => void;
  };
  createHeaderTheme: () => Theme;
}

export const Mobile: React.FC<MobileProps> = ({
  tree,
  open,
  HeaderBarProps,
  onClose,
  MobileProps: { customPanel, onCustomPanelBack } = {},
  createHeaderTheme,
}) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);
  const rootItem: FlatTree = {
    id: null,
    order: 1,
    parent: null,
    name: 'Alle Themen',
    level: -1,
    link: null,
    button: null,
    related: null,
    root: null,
    renderInitial: false,
  };
  const [showHeaderBar, setShowHeaderBar] = useState<boolean>(false);
  const [panels, setPanels] = useState<Array<FlatTree>>([rootItem]);
  const showBackLink = panels.find((item: FlatTree) => item.level > -1) !== undefined;
  const isActive = (item: FlatTree) => panels.indexOf(item) !== -1;
  const sx = Object.assign({}, styles.header, styles.isMobile);

  const nextPanel = (event: SyntheticEvent, item: FlatTree) => {
    event.preventDefault();
    setPanels((panels) => [...panels, item]);
  };

  const onBackClick = useCallback(() => {
    setPanels((panels) => [...panels].slice(0, -1));
  }, []);

  const onBackKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        onBackClick();
      }
    },
    [onBackClick]
  );

  const onExited = useCallback(() => setShowHeaderBar(false), []);
  const onEnter = useCallback(() => setShowHeaderBar(true), []);

  return (
    <>
      <Box sx={sx}>
        <Fade appear in={open} timeout={{ enter: 360, exit: 260 }} onExited={onExited} onEnter={onEnter}>
          <Box>
            {showHeaderBar && (
              <Header createTheme={createHeaderTheme}>
                <HeaderBar
                  mobileIcon={HeaderBarProps.mobileIcon}
                  id={HeaderBarProps.id}
                  searchButton={HeaderBarProps.searchButton}
                  menuButton={
                    <IconButton component="li" role="link" tabIndex={0} onClick={onClose}>
                      <Close />
                    </IconButton>
                  }
                />
                <Box bgcolor="background.default" height="54px" overflow="hidden" position="relative">
                  <Box paddingX={{ xs: 5, md: 8 }}>{HeaderBarProps.level1Navigation}</Box>
                  <Slide in={showBackLink === true} direction="left" appear={true} unmountOnExit mountOnEnter>
                    <Box
                      position="absolute"
                      left={0}
                      right={0}
                      bottom={0}
                      top={0}
                      bgcolor="background.default"
                      paddingX={{ xs: 5, md: 8 }}
                    >
                      <Button
                        size="header"
                        onClick={onBackClick}
                        tabIndex={0}
                        onKeyPress={onBackKeyPress}
                        startIcon={<ArrowLeft />}
                      >
                        Zurück
                      </Button>
                    </Box>
                  </Slide>
                  <Slide in={!!customPanel} direction="left" appear={true} unmountOnExit mountOnEnter>
                    <Box
                      position="absolute"
                      left={0}
                      right={0}
                      bottom={0}
                      top={0}
                      bgcolor="background.default"
                      paddingX={{ xs: 5, md: 8 }}
                    >
                      <Button
                        size="header"
                        onClick={onCustomPanelBack}
                        tabIndex={0}
                        onKeyPress={onBackKeyPress}
                        startIcon={<ArrowLeft />}
                      >
                        Zurück
                      </Button>
                    </Box>
                  </Slide>
                </Box>
              </Header>
            )}
          </Box>
        </Fade>
      </Box>
      <Flyout
        open={open}
        anchor="top"
        sx={styles.isMobile}
        transitionDuration={{ enter: 500, exit: 500 }}
        SlideProps={{
          unmountOnExit: true,
        }}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            height: '100%',
          },
        }}
        onClose={onClose}
        role="navigation"
      >
        <Slide
          in
          direction="down"
          style={{
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // Must be a number, otherwise the animation doesn't work
            transitionDelay: 400,
          }}
        >
          <Box sx={styles.content}>
            <Box sx={styles.panels}>
              <TransitionGroup component={null}>
                {panels.map((item: FlatTree, panelIndex: number) => {
                  const items = itemsByParent(tree, item.id);
                  return (
                    <Slide appear={panelIndex > 0} direction="left" key={item.id} timeout={{ enter: 360, exit: 360 }}>
                      <Box sx={styles.panel} style={{ zIndex: panelIndex }}>
                        <Box padding={{ xs: 5 }} marginBottom={{ xs: '126px' }}>
                          <MenuLinkList>
                            {items
                              .sort((a: FlatTree, b: FlatTree) => a.order - b.order)
                              .map((item: FlatTree) => {
                                return item.link({
                                  nextPanel: (e: SyntheticEvent) => nextPanel(e, item),
                                  isLast: isLast(tree, item.id),
                                  isActive: isActive(item),
                                  variant: 'mobile',
                                });
                              })}
                          </MenuLinkList>
                        </Box>
                      </Box>
                    </Slide>
                  );
                })}
                {!!customPanel && (
                  <Slide direction="left" timeout={{ enter: 360, exit: 360 }}>
                    <Box sx={styles.customPanel}>
                      <Box py={8} px={{ xs: 5, md: 8 }}>
                        {customPanel}
                      </Box>
                    </Box>
                  </Slide>
                )}
              </TransitionGroup>
            </Box>
            <Divider />
          </Box>
        </Slide>
        <Box
          display="flex"
          flexDirection="column"
          gap={6}
          paddingLeft={{ xs: 8 }}
          paddingRight={{ xs: 8 }}
          paddingBottom={{ xs: 7 }}
          paddingTop={{ xs: 5 }}
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          zIndex={10}
          sx={{
            background: 'rgba(255, 255, 255, 0.72)',
          }}
        >
          {HeaderBarProps.accessibilityLinks}
          {HeaderBarProps.button}
        </Box>
      </Flyout>
    </>
  );
};
